import React, { useEffect } from "react";
import { ConfigProvider, message } from "antd";
import { SWRConfig } from "swr";
import usePersistentState from "./App/AdminPortal/hooks/usePersistentState";
import UserContext from "./App/Contexts/UserContext";
import { fetcher } from "./App/AdminPortal/utils/fetcher";
import { theme } from "./App/AdminPortal/utils/theme";
import initAxiosGlobalConfigs, { setMsalInstance } from "./App/API/axios.Config";
import { PublicClientApplication } from "@azure/msal-browser";
import { getMsalConfig } from "./authConfig";
import "./App.css";
import laggy from "./App/middlewares/laggy";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { getRoleFromAuth, getUserType } from './utils/commonUtils';
import { RoutesLookup } from './App/Routes/RoutesLookup';
import { DEFAULT_USER_TYPE, USER_TYPES } from './App/Constants/ApplicationConstants';
import Router from './App/Routes/Router';
import { CartProvider } from '../src/App/Contexts/CartContext';
import { doLogout } from './utils/manageUserData';
import { GET_ADMIN_CLAIM_DATA, GET_AGENT_CLAIM_DATA } from './App/API/paths';
import { BASE_URL } from './App/Constants/EnvironmentVariableProvider';
import axios from 'axios';

const getMsalInstance = (configType) => {
  const msalConfig = getMsalConfig(configType);
  return new PublicClientApplication(msalConfig);
};

const MsalWrapperHOC = React.memo(({ configType, children }) => {
  const msalInstance = React.useMemo(
    () => getMsalInstance(configType),
    [configType]
  );

  if (!msalInstance) {
    console.error('Failed to initialize MSAL instance');
    return null;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
});

const AppContainer = () => {
  const { instance } = useMsal();
  const [auth, setAuth] = usePersistentState("auth", {
    initialValue: {},
    isSession: false
  });
  
  
  useEffect(()=>{message.config({top:100})})
  const userType = getUserType(getRoleFromAuth(auth))

  const configType = React.useMemo(() => {
    if (window.location.pathname === RoutesLookup.ADMIN_LOGIN || userType === USER_TYPES.ADMIN) {
      return USER_TYPES.ADMIN
    } else if (window.location.pathname === RoutesLookup.AGENT_LOGIN || userType === USER_TYPES.AGENT) {
      return USER_TYPES.AGENT
    }
    return DEFAULT_USER_TYPE
  }, [userType]);

  // Add window focus event handler for SSO logout sync
  useEffect(() => {
    const handleWindowFocus = async () => {
      if (auth?.token && instance) {
        try {
          // Try to get a token silently to check if user is still authenticated
          const account = instance.getActiveAccount();
          if (account) {
            await instance.ssoSilent({
              scopes: ["openid", "profile"],
              loginHint: account.username,
            });
          }
        } catch (error) {
          // If silent SSO fails, user has been logged out elsewhere
          if (error.errorMessage?.includes("interaction_required") || 
              error.errorMessage?.includes("login_required")) {
            console.log("User has been logged out elsewhere. Logging out locally...");
            doLogout(instance, setAuth);
          }
        }
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [auth, instance, setAuth]);

  // Set up axios interceptors immediately when instance is available
  useEffect(() => {
    if (instance) {
      setMsalInstance(instance);
    }
  }, [instance]);

  // Separate useEffect for auth restoration - runs immediately on mount
  useEffect(() => {
    if (instance && !auth?.token) {
      const restoreAuth = async () => {
        try {
          const storedAccount = localStorage.getItem('account');
          const storedToken = localStorage.getItem('token');
          
          if (storedAccount && storedToken) {
            const account = JSON.parse(storedAccount);
            const token = JSON.parse(storedToken);
            
            // Set the active account
            instance.setActiveAccount(account);
            
            // Check if token is still valid
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const expirationTime = tokenPayload.exp * 1000;
            const currentTime = Date.now();
            
            if (currentTime < expirationTime) {
              console.log("Token is still valid, restoring auth");
              // We need to call the API to get the full auth data
              // This is similar to what LoginByRole does
              const userType = getUserType(getRoleFromAuth({})); // Get default user type
              let urlForGet = `${BASE_URL}${GET_AGENT_CLAIM_DATA}`;
              if (userType === USER_TYPES.ADMIN) {
                urlForGet = `${BASE_URL}${GET_ADMIN_CLAIM_DATA}`;
              }
              
              try {
                const response = await axios.get(urlForGet, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                });
                
                if (response?.data?.success) {
                  console.log("Auth restored successfully");
                  setAuth(response.data);
                  initAxiosGlobalConfigs(response.data);
                } else {
                  console.log("Auth restoration failed - API returned error");
                  localStorage.removeItem('account');
                  localStorage.removeItem('token');
                }
              } catch (error) {
                console.error("Auth restoration failed - API call failed", error);
                localStorage.removeItem('account');
                localStorage.removeItem('token');
              }
            } else {
              console.log("Token expired, clearing auth");
              // Clear expired tokens
              localStorage.removeItem('account');
              localStorage.removeItem('token');
            }
          }
        } catch (error) {
          console.error("Auth restoration failed", error);
        }
      };
      
      restoreAuth();
    }
  }, [instance, auth?.token, setAuth]); // Depend on both instance and auth token

  // Separate useEffect for auth initialization
  useEffect(() => {
    if (auth?.token && instance) {
      initAxiosGlobalConfigs(auth);
    }
  }, [auth, instance]);

  useEffect(() => {
    if (!auth?.token) {
      localStorage.setItem("auth", JSON.stringify({ data: "{}" }));
    }
  }, [auth]);

  const ConditionalCartProvider = (props) => {
    const { children } = props
    return userType === USER_TYPES.ADMIN ? (
      <>{children}</>
    ) : (
      <CartProvider>{children}</CartProvider>
    );
  };

  return (
    <SWRConfig
      value={{
        fetcher: fetcher,
        onError: (error, key) => {
          if (error) {
            // TODO:  Add Proper error handling in this middleware
            message.error("Internal server Error!", error);
          }
        },
        use: [laggy],
      }}
    >
      <UserContext.Provider value={{ auth, setAuth, instance }}>
        <ConfigProvider theme={theme}>
          <MsalWrapperHOC configType={configType}>
            <ConditionalCartProvider>
              <Router />
            </ConditionalCartProvider>
          </MsalWrapperHOC>
        </ConfigProvider>
      </UserContext.Provider>
    </SWRConfig>
  );
};

export default AppContainer;
